import { ElementCenter, Particle, SparkParams } from './types';
import { defaultColors, getParamsForElement, hoverColors } from './sparkParams';

// Enhanced getCenter function that returns both center point and radius info
export const getCenter = (el: Element, customParams?: Record<string, SparkParams>): ElementCenter => {
  const r = el.getBoundingClientRect();
  const hovered = el.matches(':hover');
  const params = customParams ? getParamsForElement(el, customParams) : getParamsForElement(el);
  
  // Get relevant parameters based on hover state
  const baseSpread = hovered ? params.hoverSpread : params.defaultSpread;
  const horizontalSpread = hovered ? params.hoverHorizontalSpread : params.defaultHorizontalSpread;
  const verticalSpread = hovered ? params.hoverVerticalSpread : params.defaultVerticalSpread;
  
  // Apply global spread multipliers
  const effectiveBaseWidth = r.width * params.globalHorizontalSpread * params.globalSpread * baseSpread * horizontalSpread;
  const effectiveBaseHeight = r.height * params.globalVerticalSpread * params.globalSpread * baseSpread * verticalSpread;
  
  // Apply stretch factors
  const horizontalStretchFactor = hovered ? params.hoverHorizontalStretch : params.defaultHorizontalStretch;
  const verticalStretchFactor = hovered ? params.hoverVerticalStretch : params.defaultVerticalStretch;
  
  // Apply global stretch factors
  const effectiveHorizontalStretch = horizontalStretchFactor * params.globalHorizontalStretch;
  const effectiveVerticalStretch = verticalStretchFactor * params.globalVerticalStretch;
  
  // Use the larger dimension for scaling (maintains aspect ratio)
  const refSize = 120; // Reference size for scaling calculation
  const scaleFactor = Math.max(effectiveBaseWidth, effectiveBaseHeight) / refSize;
  
  // Calculate min and max radius for particle positioning
  const baseMin = 37.5;
  const baseMax = 90;
  
  return {
    x: r.left + r.width / 2,
    y: r.top + r.height / 2,
    minR: baseMin * scaleFactor,
    maxR: baseMax * scaleFactor,
    horizontalFactor: effectiveHorizontalStretch,
    verticalFactor: effectiveVerticalStretch
  };
};

// Updated createSparkAt that uses elliptical distribution
export const createSparkAt = (
  el: Element, 
  particlesRef: React.MutableRefObject<Particle[]>,
  hoverTimestamps: Map<Element, number>,
  customParams?: Record<string, SparkParams>
) => {
  // Get element center with additional radius information
  const { x: cx, y: cy, minR, maxR, horizontalFactor, verticalFactor } = getCenter(el, customParams);
  
  // Determine if element is hovered
  const hovered = el.matches(':hover');
  
  // Track hover timestamp
  const now = Date.now();
  if (hovered && !hoverTimestamps.has(el)) {
    hoverTimestamps.set(el, now);
  } else if (!hovered) {
    hoverTimestamps.delete(el);
  }
  
  // Generate random angle for positioning
  const angle = Math.random() * 2 * Math.PI;
  
  // Calculate random distance within min/max radius range
  const distance = (minR || 0) + Math.random() * ((maxR || 0) - (minR || 0));

  // Apply an elliptical distribution using the stretch factors
  const stretchedX = Math.cos(angle) * distance * (horizontalFactor || 1);
  const stretchedY = Math.sin(angle) * distance * (1 / (verticalFactor || 1)); // Inverse for intuitive control
  
  // Apply position with stretched coordinates
  const x = cx + stretchedX;
  const y = cy + stretchedY;
  
  // Get element-specific parameters
  const params = customParams ? getParamsForElement(el, customParams) : getParamsForElement(el);
  
  // Create particles with randomized velocity factors
  const vx = (Math.random() - 0.5) * 0.02;
  const vy = (Math.random() - 0.5) * 0.02;
  
  // Choose color palette based on hover state
  const palette = hovered ? hoverColors : defaultColors;
  const color = palette[Math.floor(Math.random() * palette.length)];
  
  // Random particle size within range
  const particleSize = params.minParticleSize + Math.random() * (params.maxParticleSize - params.minParticleSize);

  // Create the particle
  particlesRef.current.push({ 
    x, y, vx, vy, 
    life: 180 + Math.random() * 60, 
    r: particleSize, 
    color, 
    hovered 
  });
};

// Updated function to consider density factors when determining emission rate
export const calculateEmissionRate = (el: Element, now: number, customParams?: Record<string, SparkParams>): number => {
  const hovered = el.matches(':hover');
  const params = customParams ? getParamsForElement(el, customParams) : getParamsForElement(el);
  
  // Select appropriate density based on hover state
  const stateDensity = hovered ? params.hoverDensity : params.defaultDensity;
  
  // Apply global density factor
  const effectiveDensity = stateDensity * params.globalDensity;
  
  // Base emission rate scaled by density
  const baseRate = 0.05 * effectiveDensity;
  
  // Add time-based variation for more natural effect
  const t = (now % 4000) / 4000;
  return baseRate + 0.05 * effectiveDensity * (1 - Math.cos(t * 2 * Math.PI)) / 2;
};

// Keep the updateCanvasFilter function
export const updateCanvasFilter = (
  canvas: HTMLCanvasElement,
  hoverTimestamps: Map<Element, number>,
  elements: NodeListOf<Element>
) => {
  const now = Date.now();
  
  let maxElapsed = 0;
  elements.forEach(el => {
    if (el.matches(':hover') && hoverTimestamps.has(el)) {
      const elapsed = now - hoverTimestamps.get(el)!;
      if (elapsed > maxElapsed) maxElapsed = elapsed;
    }
  });

  let grayscale = 65;
  let glowStrength = 0.4;
  if (maxElapsed > 0) {
    const t = Math.min(maxElapsed / 6000, 1);
    grayscale = t < 0.5833
      ? 65 + (100 - 65) * (t / 0.5833)
      : 100 - ((t - 0.5833) / 0.4167) * 100;
    glowStrength = t < 0.5
      ? 0.4 + 0.3 * (t / 0.5)
      : 0.7 - 0.2 * ((t - 0.5) / 0.5);
  }

  const activePalette = maxElapsed > 0 ? hoverColors : defaultColors;
  const glowColor = activePalette[Math.floor(Math.random() * activePalette.length)];
  canvas.style.filter = `grayscale(${grayscale}%) drop-shadow(0 0 6px rgba(${glowColor}, ${glowStrength}))`;
};
